import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang, categoryLabels } from "@/i18n";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/useSEO";
import { ProductCard } from "@/components/ProductCard";
import { toast } from "sonner";
import { Truck, ShieldCheck, BadgeCheck, Heart, Star } from "lucide-react";

const Stars = ({ value, onSelect, size = "w-5 h-5" }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onSelect} onClick={() => onSelect && onSelect(n)}
        data-testid={onSelect ? `rating-star-${n}` : undefined}
        className={onSelect ? "cursor-pointer" : "cursor-default"}>
        <Star className={`${size} ${n <= value ? "fill-[#C5A880] text-[#C5A880]" : "text-[#D9D2C7]"}`} strokeWidth={1.4} />
      </button>
    ))}
  </div>
);

export default function ProductDetail() {
  const { id } = useParams();
  const { t, tl, lang } = useLang();
  const { addItem } = useCart();
  const { format } = useCurrency();
  const { has, toggle } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mainImg, setMainImg] = useState(0);
  const [related, setRelated] = useState([]);
  const [reviewData, setReviewData] = useState({ reviews: [], average: 0, count: 0 });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  useSEO(product ? {
    title: `${tl(product.name)} · Mimi Silver`,
    description: tl(product.description),
    jsonLd: {
      "@context": "https://schema.org", "@type": "Product",
      name: tl(product.name), description: tl(product.description), image: product.images,
      offers: { "@type": "Offer", priceCurrency: "CHF", price: product.price, availability: "https://schema.org/InStock" },
      ...(reviewData.count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: reviewData.average, reviewCount: reviewData.count } } : {}),
    },
  } : {});

  const loadReviews = () => api.get(`/products/${id}/reviews`).then((res) => setReviewData(res.data)).catch(() => {});

  useEffect(() => {
    setNotFound(false); setProduct(null); setMainImg(0);
    api.get(`/products/${id}`).then((res) => {
      setProduct(res.data);
      api.get("/products", { params: { category: res.data.category } })
        .then((r) => setRelated(r.data.filter((p) => p.id !== res.data.id).slice(0, 4)));
    }).catch(() => setNotFound(true));
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    try {
      await api.post("/reviews", { product_id: id, rating, comment });
      setComment(""); setRating(5); toast.success(t("review_thanks")); loadReviews();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-5 text-center px-4" data-testid="product-not-found">
        <h1 className="font-serif-display text-4xl text-[#1A1F2C]">404</h1>
        <Link to="/shop" className="bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase px-10 py-4 hover:bg-[#C5A880] transition-colors">{t("shop_all")}</Link>
      </div>
    );
  }
  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-[#6B7280]">…</div>;

  const images = product.images?.length ? product.images : [""];

  return (
    <div className="px-4 sm:px-8 lg:px-16 py-12 lg:py-16 fade-up">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 max-w-6xl mx-auto">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/5] bg-[#F0EAE1] overflow-hidden group">
            <img src={images[mainImg]} alt={tl(product.name)} data-testid="product-detail-image"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((img, i) => (
                <button key={i} onClick={() => setMainImg(i)} data-testid={`thumb-${i}`}
                  className={`w-20 h-24 overflow-hidden border ${mainImg === i ? "border-[#C5A880]" : "border-[#EAE6E1]"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex flex-col">
          <span className="text-xs tracking-luxe uppercase text-[#C5A880] mb-3">{categoryLabels[product.category]?.[lang]}</span>
          <h1 data-testid="product-detail-name" className="font-serif-display text-4xl lg:text-5xl text-[#1A1F2C] leading-none mb-3">{tl(product.name)}</h1>
          {reviewData.count > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <Stars value={Math.round(reviewData.average)} size="w-4 h-4" />
              <span className="text-sm text-[#6B7280]">{reviewData.average} · {reviewData.count}</span>
            </div>
          )}
          <span data-testid="product-detail-price" className="text-2xl text-[#1A1F2C] mb-6">{format(product.price)}</span>
          <p className="text-base text-[#6B7280] leading-relaxed mb-8 max-w-md">{tl(product.description)}</p>

          <div className="flex items-center gap-3">
            <button data-testid="product-detail-add-to-cart"
              onClick={() => { addItem(product); toast.success(t("added_to_cart")); }}
              className="bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase py-4 px-12 hover:bg-[#C5A880] transition-colors duration-300">
              {t("add_to_cart")}
            </button>
            <button data-testid="product-detail-wishlist" onClick={() => toggle(product.id)}
              className="w-14 h-14 flex items-center justify-center border border-[#EAE6E1] hover:border-[#C5A880] transition-colors">
              <Heart className={`w-5 h-5 ${has(product.id) ? "fill-[#C5A880] text-[#C5A880]" : "text-[#1A1F2C]"}`} strokeWidth={1.5} />
            </button>
          </div>

          <div className="mt-10 pt-8 border-t border-[#EAE6E1] space-y-4">
            {[{ icon: Truck, label: t("free_shipping") }, { icon: ShieldCheck, label: t("secure_payment") }, { icon: BadgeCheck, label: t("authentic") }].map((x, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-[#6B7280]">
                <x.icon className="w-4 h-4 text-[#C5A880]" strokeWidth={1.4} /> {x.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div className="max-w-6xl mx-auto mt-20" data-testid="reviews-block">
        <h2 className="font-serif-display text-3xl text-[#1A1F2C] mb-8">{t("reviews")}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-5">
            {reviewData.reviews.length === 0 && <p className="text-sm text-[#6B7280]">{t("no_reviews")}</p>}
            {reviewData.reviews.map((r) => (
              <div key={r.id} className="border-b border-[#EAE6E1] pb-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs tracking-luxe uppercase text-[#1A1F2C]">{r.author}</span>
                  <Stars value={r.rating} size="w-3.5 h-3.5" />
                </div>
                {r.comment && <p className="text-sm text-[#6B7280] leading-relaxed">{r.comment}</p>}
              </div>
            ))}
          </div>
          <div className="bg-white border border-[#EAE6E1] p-7 h-fit">
            <h3 className="text-xs tracking-luxe uppercase text-[#C5A880] mb-4">{t("write_review")}</h3>
            {user ? (
              <form onSubmit={submitReview} data-testid="review-form" className="space-y-4">
                <div>
                  <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("your_rating")}</label>
                  <Stars value={rating} onSelect={setRating} />
                </div>
                <div>
                  <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("your_review")}</label>
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} data-testid="review-comment"
                    className="w-full bg-[#FAF9F6] border border-[#EAE6E1] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A880]" />
                </div>
                <button type="submit" data-testid="review-submit"
                  className="bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase py-3 px-8 hover:bg-[#C5A880] transition-colors">{t("submit_review")}</button>
              </form>
            ) : (
              <Link to="/login" data-testid="login_to_review" className="text-sm text-[#1A1F2C] border-b border-[#1A1F2C] hover:text-[#C5A880]">{t("login_to_review")}</Link>
            )}
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-6xl mx-auto mt-24">
          <h2 className="font-serif-display text-3xl text-[#1A1F2C] mb-8">{t("related_title")}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
            {related.map((p) => <div key={p.id} data-testid={`related-product-${p.id}`}><ProductCard product={p} /></div>)}
          </div>
        </div>
      )}
    </div>
  );
}
